export function normalizeOperacionesFilters(filters = {}) {
  return {
    idPeriodo: Number(filters.idPeriodo ?? 0),
    idProv: Number(filters.idProv ?? 0),
  };
}

export function areOperacionesFiltersEqual(a = {}, b = {}) {
  return a.idPeriodo === b.idPeriodo && a.idProv === b.idProv;
}
const OPERACIONES_DEFAULT_PERIODO = Number(
  process.env.VUE_APP_LIQUIDACION_REPORTES_DEFAULT_PERIODO || 0,
);

export function getDefaultOperacionesFilters() {
  const idPeriodo =
    Number.isFinite(OPERACIONES_DEFAULT_PERIODO) &&
    OPERACIONES_DEFAULT_PERIODO > 0
      ? OPERACIONES_DEFAULT_PERIODO
      : 0;

  return {
    idPeriodo,
    idProv: 0,
  };
}

function normalizeImportValue(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeHeaderKey(key) {
  return String(key || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getRowValue(raw, ...aliases) {
  const normalizedAliases = aliases.map(normalizeHeaderKey);

  for (const [key, value] of Object.entries(raw)) {
    if (normalizedAliases.includes(normalizeHeaderKey(key))) {
      return value;
    }
  }

  return undefined;
}

function isBlankRawRow(raw) {
  return Object.values(raw).every(
    (value) =>
      value === null || value === undefined || String(value).trim() === "",
  );
}

export function validateImportRow(row) {
  const ruc = normalizeImportValue(row.ruc);
  const tienda = normalizeImportValue(row.tienda);
  const material = normalizeImportValue(row.material);
  const numeroOc = normalizeImportValue(row.numeroOc);
  const migo = normalizeImportValue(row.migo);

  if (!ruc || !tienda || !material) {
    return "Falta RUC, Tienda o Material.";
  }
  if (!numeroOc && !migo) {
    return "Falta OC y MIGO.";
  }
  if (!numeroOc) {
    return "Falta OC.";
  }
  if (!migo) {
    return "Falta MIGO.";
  }
  return "";
}
function normalizeImportId(value) {
  const raw = normalizeImportValue(value);
  if (!raw) return undefined;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

export function parseOperacionesImportRows(rawRows = []) {
  return rawRows
    .map((raw, index) => ({ raw, rowNumber: index + 2 })) // fila 1 es el encabezado
    .filter(({ raw }) => !isBlankRawRow(raw))
    .map(({ raw, rowNumber }) => {
      const row = {
        rowNumber,
        ruc: normalizeImportValue(getRowValue(raw, "RUC")),
        tienda: normalizeImportValue(getRowValue(raw, "Tienda")).toUpperCase(),
        material: normalizeImportValue(getRowValue(raw, "Material")),
        numeroOc: normalizeImportValue(getRowValue(raw, "OC", "Numero OC", "N° OC")),
        migo: normalizeImportValue(getRowValue(raw, "MIGO")),
        idLiquidacionCab: normalizeImportId(
          getRowValue(raw, "ID Liquidación", "IdLiquidacionCab", "Id Liquidacion Cab"),
        ),
        codigoTienda: normalizeImportId(
          getRowValue(raw, "Cód. Tienda", "Codigo Tienda", "CodigoTienda"),
        ),
      };
      row.error = validateImportRow(row);
      return row;
    });
}


export const OPERACIONES_IMPORT_MAX_ROWS = 500;

export function assertImportRowLimit(rows, limit = OPERACIONES_IMPORT_MAX_ROWS) {
  if (!Array.isArray(rows) || rows.length <= limit) return;

  const error = new Error(
    `El archivo tiene ${rows.length} filas y el máximo permitido es ${limit}. Divide el archivo en partes más pequeñas.`,
  );
  error.code = "IMPORT_ROW_LIMIT_EXCEEDED";
  throw error;
}

export function buildGuardarOcMigoMasivoPayload(idPeriodo, rows = [], usuario) {
  return {
    idPeriodo: Number(idPeriodo),
    usuario: usuario || "SISTEMA",
    items: rows
      .filter((row) => !row.error)
      .map((row) => ({
        ruc: row.ruc,
        tienda: row.tienda,
        material: row.material,
        numeroOc: row.numeroOc,
        migo: row.migo,
        idLiquidacionCab: row.idLiquidacionCab ?? null,
        codigoTienda: row.codigoTienda ?? null,   
      })),
  };
}

function matchKey(ruc, tienda, material) {
  return [
    normalizeImportValue(ruc).toUpperCase(),
    normalizeImportValue(tienda).toUpperCase(),
    normalizeImportValue(material).toUpperCase(),
  ].join("|");
}

export function mapNoEncontradosToRows(noEncontrados, sentRows = []) {
  const list = Array.isArray(noEncontrados) ? noEncontrados : [];
  const byKey = new Map();

  sentRows.forEach((row) => {
    byKey.set(matchKey(row.ruc, row.tienda, row.material), row);
  });

  return list.map((item) => {
    const original = byKey.get(matchKey(item.ruc, item.tienda, item.material));
    return {
      rowNumber: original ? original.rowNumber : "-",
      ruc: item.ruc,
      tienda: item.tienda,
      material: item.material,
      numeroOc: item.numeroOc,
      migo: item.migo,
      reason:
        item.motivo ||
        "No se encontró una OS que coincida con RUC + Tienda + Material en este periodo.",
    };
  });
}

export function applyValidationResult(rows = [], noEncontrados) {
  const list = Array.isArray(noEncontrados) ? noEncontrados : [];
  const noMatchByKey = new Map(
    list.map((item) => [matchKey(item.ruc, item.tienda, item.material), item]),
  );

  rows.forEach((row) => {
    if (row.error) return; 
    const noMatch = noMatchByKey.get(matchKey(row.ruc, row.tienda, row.material));
    if (noMatch) {
      row.error = noMatch.motivo || "No se encontró una OS que coincida en este periodo.";
    }
  });
}